(() => {
  const PENDING_CART_KEY = 'moveboard_pending_cart';
  const SOFT_CART_KEY = 'moveboard_soft_cart_v1';
  const moneyFmt = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'INR',
    maximumFractionDigits: 0,
  });

  const getCustomerId = () =>
    document.querySelector('meta[name="moveboard-customer-id"]')?.content?.trim() || '';
  const getCustomerEmail = () => {
    const meta = document.querySelector('meta[name="moveboard-customer-email"]')?.content?.trim();
    if (meta) return meta;
    const pop = document.querySelector('.site-header__account-pop-email')?.textContent?.trim();
    return pop || '';
  };

  const getApiBase = () => {
    const preorder =
      document.querySelector('meta[name="moveboard-preorder-api"]')?.content?.trim() || '';
    if (!preorder) return '';
    try {
      const url = new URL(preorder, window.location.origin);
      url.pathname = url.pathname.replace(/\/preorder\/?$/, '') || '/';
      if (url.pathname.endsWith('/')) url.pathname = url.pathname.slice(0, -1) || '';
      return url.origin + (url.pathname === '/' ? '' : url.pathname);
    } catch {
      return preorder.replace(/\/preorder\/?$/, '');
    }
  };

  /* Order reads and cancellations must be signed by Shopify, so they travel
     through the app proxy. Local theme preview cannot be proxied — Shopify
     can't reach 127.0.0.1 — so it talks to the Worker directly. */
  const APP_PROXY_BASE = '/apps/moveboard';
  const getOrdersApiBase = () => (isLocalPreview ? getApiBase() : APP_PROXY_BASE);

  const ordersEndpoint = (path, params) => {
    const base = getOrdersApiBase();
    if (!base) return null;
    const url = new URL(`${base}${path}`, location.origin);
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value);
    });
    return url.toString();
  };

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const getShopOrigin = () =>
    document.querySelector('meta[name="moveboard-shop-origin"]')?.content?.trim().replace(/\/$/, '') ||
    (window.Shopify?.shop ? `https://${window.Shopify.shop}` : location.origin);

  const isLocalPreview =
    location.hostname === '127.0.0.1' || location.hostname === 'localhost';

  const getCartUrl = () => {
    const meta = document.querySelector('meta[name="moveboard-cart-url"]')?.content?.trim();
    if (meta) return meta;
    return `${getShopOrigin()}/cart`;
  };

  const getCartPath = () => {
    try {
      return new URL(getCartUrl(), location.origin).pathname;
    } catch {
      return '/cart';
    }
  };

  const buildLoginUrl = (resumeParam, returnPath) => {
    const path = returnPath || location.pathname;
    const returnTo = `${path}?${resumeParam}=1`;
    /* Local theme proxy returns HTTP 401 for /customer_authentication — use shop host. */
    const base = isLocalPreview
      ? `${getShopOrigin()}/customer_authentication/login`
      : '/customer_authentication/login';
    return `${base}?return_to=${encodeURIComponent(returnTo)}`;
  };

  const navigateToCart = () => {
    if (!getCustomerId()) {
      window.location.href = buildLoginUrl('resume_cart_view', getCartPath());
      return;
    }
    window.location.href = getCartUrl();
  };

  const isCartPage = () => /\/cart\/?$/.test(location.pathname);

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  /* -------- Cart -------- */
  const isCartEnabled =
    document.querySelector('meta[name="moveboard-cart-enabled"]')?.content === 'true';

  const cartDrawer = isCartEnabled ? document.querySelector('[data-cart-drawer]') : null;
  const cartEls = cartDrawer
    ? {
        body: cartDrawer.querySelector('[data-cart-body]'),
        loading: cartDrawer.querySelector('[data-cart-loading]'),
        empty: cartDrawer.querySelector('[data-cart-empty]'),
        list: cartDrawer.querySelector('[data-cart-list]'),
        footer: cartDrawer.querySelector('[data-cart-footer]'),
        subtotal: cartDrawer.querySelector('[data-cart-subtotal]'),
        footnote: cartDrawer.querySelector('[data-cart-footnote]'),
        checkoutShopify: cartDrawer.querySelector('[data-cart-checkout-shopify]'),
        checkoutReserve: cartDrawer.querySelector('[data-cart-checkout-reserve]'),
      }
    : null;

  const cartCountEl = isCartEnabled ? document.querySelector('[data-cart-count]') : null;

  const readSoftCart = () => {
    try {
      const items = JSON.parse(localStorage.getItem(SOFT_CART_KEY) || '[]');
      return Array.isArray(items) ? items : [];
    } catch {
      return [];
    }
  };

  const writeSoftCart = (items) => {
    try {
      localStorage.setItem(SOFT_CART_KEY, JSON.stringify(items));
    } catch {
      /* ignore */
    }
  };

  const softCartCount = (items = readSoftCart()) =>
    items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

  const softCartToView = (items = readSoftCart()) => {
    const mapped = items.map((item) => {
      const qty = Math.min(5, Math.max(1, Number(item.quantity) || 1));
      const unit = Number(item.price) || 0;
      const title = item.productTitle || item.title || 'Moveboard';
      const variant = item.variantTitle && item.variantTitle !== 'Default Title' ? item.variantTitle : '';
      return {
        product_title: variant ? `${title} — ${variant}` : title,
        title,
        quantity: qty,
        final_line_price: unit * qty,
        image: item.image || '',
        url: item.url || '#',
        soft: true,
      };
    });
    return {
      item_count: softCartCount(items),
      items_subtotal_price: mapped.reduce((sum, item) => sum + item.final_line_price, 0),
      items: mapped,
      soft: true,
    };
  };

  const addSoftCartItem = ({
    variantId,
    quantity = 1,
    productTitle,
    variantTitle,
    price,
    image,
    url,
  }) => {
    const id = String(variantId);
    const qty = Math.min(5, Math.max(1, Number(quantity) || 1));
    const items = readSoftCart();
    const existing = items.find((item) => String(item.variantId) === id);
    if (existing) {
      existing.quantity = Math.min(5, (Number(existing.quantity) || 0) + qty);
      if (productTitle) existing.productTitle = productTitle;
      if (variantTitle) existing.variantTitle = variantTitle;
      if (price != null) existing.price = Number(price) || 0;
      if (image) existing.image = image;
      if (url) existing.url = url;
    } else {
      items.push({
        variantId: id,
        quantity: qty,
        productTitle: productTitle || 'Moveboard',
        variantTitle: variantTitle || '',
        price: Number(price) || 0,
        image: image || '',
        url: url || location.pathname,
      });
    }
    writeSoftCart(items);
    return softCartToView(items);
  };

  const setCartCount = (count) => {
    if (!cartCountEl) return;
    const n = Number(count) || 0;
    cartCountEl.textContent = String(n);
    cartCountEl.setAttribute('data-empty', n > 0 ? 'false' : 'true');
  };

  const refreshCartBadge = async () => {
    const soft = softCartCount();
    try {
      const cart = await fetchCart();
      setCartCount((cart.item_count || 0) + soft);
      return cart;
    } catch {
      setCartCount(soft);
      return null;
    }
  };

  const closeCartDrawer = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    document.body.classList.remove('mb-drawer-open');
    window.setTimeout(() => {
      cartDrawer.hidden = true;
      cartDrawer.setAttribute('aria-hidden', 'true');
    }, 260);
  };

  const renderCart = (cart) => {
    if (!cartEls) return;
    const items = cart?.items || [];
    const isSoft = Boolean(cart?.soft);
    setCartCount(isSoft ? softCartCount() : (cart?.item_count || 0) + softCartCount());
    cartEls.loading.hidden = true;

    if (!items.length) {
      cartEls.empty.hidden = false;
      cartEls.list.hidden = true;
      cartEls.footer.hidden = true;
      cartEls.list.innerHTML = '';
      return;
    }

    cartEls.empty.hidden = true;
    cartEls.list.hidden = false;
    cartEls.footer.hidden = false;
    cartEls.subtotal.textContent = moneyFmt.format((cart.items_subtotal_price || 0) / 100);

    if (cartEls.footnote) {
      cartEls.footnote.textContent = isSoft
        ? 'Shopify cart inventory is blocked for this product. Place a reservation to continue (same as Order now).'
        : 'Taxes and shipping calculated at checkout.';
    }
    if (cartEls.checkoutShopify) {
      cartEls.checkoutShopify.hidden = isSoft;
    }
    if (cartEls.checkoutReserve) {
      cartEls.checkoutReserve.hidden = !isSoft;
    }

    cartEls.list.innerHTML = items
      .map((item) => {
        const img = item.image
          ? `<img src="${item.image}" alt="" width="68" height="68" loading="lazy">`
          : '';
        return `<li class="mb-cart-item">
          <div class="mb-cart-item__media">${img}</div>
          <div>
            <a class="mb-cart-item__title" href="${item.url || '#'}">${item.product_title || item.title}</a>
            <p class="mb-cart-item__meta">Qty ${item.quantity}</p>
            <p class="mb-cart-item__price">${moneyFmt.format((item.final_line_price || item.line_price || 0) / 100)}</p>
          </div>
        </li>`;
      })
      .join('');
  };

  const fetchCart = async () => {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Unable to load cart.');
    return res.json();
  };

  const cartItemsForSync = (cart) =>
    (cart?.items || [])
      .map((item) => ({
        id: item.variant_id,
        quantity: Math.min(5, Math.max(0, Number(item.quantity) || 0)),
      }))
      .filter((item) => item.id && item.quantity > 0);

  const customerCartEndpoint = () => ordersEndpoint('/customer-cart', isLocalPreview ? { customerId: getCustomerId(), email: getCustomerEmail() } : {});

  const syncCustomerCartToServer = async (cart) => {
    if (!getCustomerId() || isLocalPreview) return false;
    const url = customerCartEndpoint();
    if (!url) return false;
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: cartItemsForSync(cart) }),
      });
      const payload = await res.json().catch(() => ({}));
      return payload?.ok === true;
    } catch {
      return false;
    }
  };

  const setCartEmptySyncUi = ({ title, showCopy = true }) => {
    const titleEl = document.querySelector('[data-cart-sync-title]');
    const copyEl = document.querySelector('[data-cart-sync-copy]');
    if (titleEl && title) titleEl.textContent = title;
    if (copyEl) copyEl.hidden = !showCopy;
  };

  const mergeCustomerCartFromServer = async () => {
    if (!getCustomerId() || isLocalPreview) return { merged: false, apiAvailable: true };
    const url = customerCartEndpoint();
    if (!url) return { merged: false, apiAvailable: true };

    let remoteItems = [];
    let apiAvailable = true;
    try {
      const res = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      const payload = await res.json().catch(() => ({}));
      if (payload?.message === 'Not found') {
        apiAvailable = false;
      } else if (payload?.ok && Array.isArray(payload.items)) {
        remoteItems = payload.items;
      }
    } catch {
      apiAvailable = false;
    }

    const local = await fetchCart().catch(() => ({ items: [], item_count: 0 }));
    const localItems = local?.items || [];
    const localByVariant = new Map(
      localItems.map((item) => [String(item.variant_id), Math.min(5, Number(item.quantity) || 0)]),
    );

    const merged = new Map();
    remoteItems.forEach((row) => {
      const id = String(row.id || '').replace(/\D/g, '');
      const qty = Math.min(5, Math.max(1, Number(row.quantity) || 0));
      if (id) merged.set(id, qty);
    });
    localItems.forEach((item) => {
      const id = String(item.variant_id);
      const qty = Math.min(5, Number(item.quantity) || 0);
      merged.set(id, Math.max(merged.get(id) || 0, qty));
    });

    const toAdd = [];
    merged.forEach((want, id) => {
      const have = localByVariant.get(id) || 0;
      if (have < want) {
        toAdd.push({ id: Number(id), quantity: want - have });
      }
    });

    if (!toAdd.length && !localItems.length && !remoteItems.length) {
      return { merged: false, apiAvailable };
    }

    if (toAdd.length) {
      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: toAdd }),
      });
      if (!addRes.ok) return { merged: false, apiAvailable };
    }

    const updated = await fetchCart().catch(() => local);
    if (apiAvailable) {
      await syncCustomerCartToServer(updated);
    }
    setCartCount(updated.item_count || 0);
    return { merged: toAdd.length > 0, apiAvailable };
  };

  const openCartDrawer = async () => {
    if (!cartDrawer || !cartEls) return;
    if (!getCustomerId()) {
      window.location.href = buildLoginUrl('resume_cart_view');
      return;
    }
    cartDrawer.hidden = false;
    cartDrawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mb-drawer-open');
    cartEls.loading.hidden = false;
    cartEls.empty.hidden = true;
    cartEls.list.hidden = true;
    cartEls.footer.hidden = true;
    requestAnimationFrame(() => cartDrawer.classList.add('is-open'));
    try {
      const soft = readSoftCart();
      const cart = await fetchCart().catch(() => null);
      if (cart?.item_count > 0) {
        renderCart(cart);
        return;
      }
      if (soft.length) {
        renderCart(softCartToView(soft));
        return;
      }
      renderCart(cart || { items: [], item_count: 0, items_subtotal_price: 0 });
    } catch (err) {
      const soft = readSoftCart();
      if (soft.length) {
        renderCart(softCartToView(soft));
        return;
      }
      cartEls.loading.hidden = true;
      cartEls.empty.hidden = false;
      cartEls.empty.querySelector('.mb-drawer__empty-copy').textContent =
        err instanceof Error ? err.message : 'Unable to load cart.';
    }
  };

  const collectAtcMeta = (variantId) => {
    const btn =
      document.querySelector(`[data-atc-open][data-default-variant-id="${variantId}"]`) ||
      document.querySelector('[data-atc-open]');
    return {
      productTitle: btn?.getAttribute('data-product-title') || document.querySelector('[data-product-title]')?.textContent?.trim() || 'Moveboard',
      variantTitle: btn?.getAttribute('data-variant-title') || '',
      price: btn?.getAttribute('data-price') || '0',
      image: btn?.getAttribute('data-image') || '',
      url: btn?.getAttribute('data-url') || location.pathname,
    };
  };

  const addToCart = async ({ variantId, quantity = 1, meta } = {}) => {
    if (!variantId) throw new Error('Missing product variant.');
    const qty = Math.min(5, Math.max(1, Number(quantity) || 1));
    const itemMeta = meta || collectAtcMeta(variantId);

    if (!getCustomerId()) {
      try {
        sessionStorage.setItem(
          PENDING_CART_KEY,
          JSON.stringify({
            variantId: String(variantId),
            quantity: qty,
            path: location.pathname,
            ...itemMeta,
          }),
        );
      } catch {
        /* ignore */
      }
      window.location.href = buildLoginUrl('resume_cart');
      return null;
    }

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: qty }],
      }),
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) {
      const raw = payload?.description || payload?.message || 'Could not add to cart.';
      /* Shopify Admin can show stock + continue-selling while Ajax cart still rejects. */
      if (/sold out|not available|inventory/i.test(raw)) {
        const softView = addSoftCartItem({
          variantId,
          quantity: qty,
          ...itemMeta,
        });
        setCartCount(softCartCount());
        window.alert(
          'This item could not be added to the Shopify cart (often inventory). Use Order now to reserve instead.',
        );
        return softView;
      }
      throw new Error(raw);
    }
    writeSoftCart([]);
    const cart = await fetchCart();
    setCartCount(cart.item_count || 0);
    await syncCustomerCartToServer(cart);
    return cart;
  };

  const swatchHexForName = (name) => {
    const key = String(name || '').toLowerCase().trim();
    const map = {
      white: '#f4f1ea',
      black: '#161616',
      'natural oak': '#c4a574',
      oak: '#c4a574',
      natural: '#c4a574',
      walnut: '#5c3a24',
      'deep walnut': '#5c3a24',
      'white ash': '#ddd6c8',
      ash: '#ddd6c8',
    };
    return map[key] || '#cfc8bb';
  };

  const findColorOptionIndex = (product) => {
    const options = product?.options || [];
    const named = options.findIndex((name) => /color|colour|finish|style|shade/i.test(String(name || '')));
    if (named >= 0) return named;
    if ((product?.variants?.length || 0) > 1 && options.length > 0) return 0;
    return -1;
  };

  const variantOptionValue = (variant, optionIndex) => {
    if (optionIndex === 0) return variant.option1;
    if (optionIndex === 1) return variant.option2;
    if (optionIndex === 2) return variant.option3;
    return '';
  };

  const initAtcModal = () => {
    const modal = document.querySelector('[data-atc-modal]');
    if (!modal) return;

    const dialog = modal.querySelector('.atc-modal__dialog');
    const titleEl = modal.querySelector('[data-atc-title]');
    const priceEl = modal.querySelector('[data-atc-price]');
    const imageEl = modal.querySelector('[data-atc-image]');
    const colorsWrap = modal.querySelector('[data-atc-colors-wrap]');
    const colorsRoot = modal.querySelector('[data-atc-colors]');
    const colorNameEl = modal.querySelector('[data-atc-color-name]');
    const colorLabelEl = modal.querySelector('[data-atc-color-label]');
    const qtyValueEl = modal.querySelector('[data-atc-qty-value]');
    const decBtn = modal.querySelector('[data-atc-qty-decrease]');
    const incBtn = modal.querySelector('[data-atc-qty-increase]');
    const submitBtn = modal.querySelector('[data-atc-submit]');
    const errorEl = modal.querySelector('[data-atc-error]');

    const state = {
      product: null,
      variants: [],
      selectedVariant: null,
      quantity: 1,
      triggerMeta: {},
      colorOptionIndex: -1,
    };

    const setError = (message) => {
      if (!errorEl) return;
      errorEl.hidden = !message;
      errorEl.textContent = message || '';
    };

    const setQty = (next) => {
      const max = 5;
      state.quantity = Math.min(max, Math.max(1, Number(next) || 1));
      if (qtyValueEl) qtyValueEl.textContent = String(state.quantity);
      if (incBtn) incBtn.disabled = state.quantity >= max;
      if (decBtn) decBtn.disabled = state.quantity <= 1;
    };

    const variantImage = (variant) =>
      variant?.featured_image?.src ||
      state.product?.featured_image ||
      state.triggerMeta.image ||
      '';

    const applyVariant = (variant) => {
      if (!variant) return;
      state.selectedVariant = variant;
      if (imageEl) {
        imageEl.src = variantImage(variant);
        imageEl.alt = state.product?.title || 'Product';
      }
      if (priceEl) {
        priceEl.textContent = moneyFmt.format((Number(variant.price) || 0) / 100);
      }
      const colorValue =
        state.colorOptionIndex >= 0
          ? variantOptionValue(variant, state.colorOptionIndex) || variant.title
          : variant.title;
      if (colorNameEl) {
        colorNameEl.textContent = colorValue ? `Selected: ${colorValue}` : '';
      }
      colorsRoot?.querySelectorAll('[data-atc-color]').forEach((btn) => {
        const isSelected = String(btn.getAttribute('data-variant-id')) === String(variant.id);
        btn.classList.toggle('is-selected', isSelected);
        btn.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      });
      if (submitBtn) submitBtn.disabled = !variant.available;
    };

    const renderColorSwatches = () => {
      if (!colorsRoot || !colorsWrap) return;
      colorsRoot.innerHTML = '';
      const idx = state.colorOptionIndex;
      const optionName =
        idx >= 0 && state.product?.options?.[idx]
          ? String(state.product.options[idx])
          : 'Option';

      if (colorLabelEl) {
        colorLabelEl.textContent =
          /color|colour|finish/i.test(optionName) ? 'Choose colour' : `Choose ${optionName.toLowerCase()}`;
      }

      if (idx < 0 || !state.variants.length) {
        colorsWrap.hidden = true;
        return;
      }

      colorsWrap.hidden = false;

      const values = [];
      state.variants.forEach((variant) => {
        const value = String(variantOptionValue(variant, idx) || variant.title || '').trim();
        if (!value || values.includes(value)) return;
        values.push(value);
      });

      values.forEach((value) => {
        const variant =
          state.variants.find(
            (item) =>
              String(variantOptionValue(item, idx) || item.title).trim() === value && item.available,
          ) ||
          state.variants.find(
            (item) => String(variantOptionValue(item, idx) || item.title).trim() === value,
          );
        if (!variant) return;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'atc-modal__chip';
        btn.setAttribute('data-atc-color', '');
        btn.setAttribute('data-variant-id', String(variant.id));
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-label', value);
        btn.style.setProperty('--swatch', swatchHexForName(value));
        if (!variant.available) btn.classList.add('is-unavailable');
        btn.innerHTML = `<span class="atc-modal__chip-swatch" aria-hidden="true"></span><span class="atc-modal__chip-label">${value}</span>`;
        btn.addEventListener('click', () => applyVariant(variant));
        colorsRoot.appendChild(btn);
      });
    };

    const closeModal = () => {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('mb-drawer-open');
      setError('');
    };

    const openModal = async (trigger) => {
      const handle = trigger.getAttribute('data-product-handle');
      if (!handle) return;

      state.triggerMeta = {
        productTitle: trigger.getAttribute('data-product-title') || '',
        url: trigger.getAttribute('data-url') || `/products/${handle}`,
        image: trigger.getAttribute('data-image') || '',
      };
      state.quantity = 1;
      setQty(1);
      setError('');
      submitBtn.disabled = true;
      submitBtn.textContent = 'Loading…';
      titleEl.textContent = state.triggerMeta.productTitle || 'Product';
      if (imageEl && state.triggerMeta.image) {
        imageEl.src = state.triggerMeta.image;
      }

      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('mb-drawer-open');
      dialog?.focus();

      try {
        const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error('Could not load product.');
        const product = await res.json();
        state.product = product;
        state.variants = product.variants || [];
        state.colorOptionIndex = findColorOptionIndex(product);
        titleEl.textContent = product.title || state.triggerMeta.productTitle;

        renderColorSwatches();

        const preferredId =
          trigger.getAttribute('data-default-variant-id') ||
          document.querySelector('form.product-form [name="id"]')?.value;
        let selected =
          state.variants.find((item) => String(item.id) === String(preferredId)) ||
          state.variants.find((item) => item.available) ||
          state.variants[0];
        applyVariant(selected);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load product.');
      } finally {
        submitBtn.textContent = 'Add to cart';
      }
    };

    modal.querySelectorAll('[data-atc-close]').forEach((el) => {
      el.addEventListener('click', () => closeModal());
    });

    decBtn?.addEventListener('click', () => setQty(state.quantity - 1));
    incBtn?.addEventListener('click', () => setQty(state.quantity + 1));

    submitBtn?.addEventListener('click', async () => {
      if (!state.selectedVariant) {
        setError('Please choose a colour.');
        return;
      }
      if (!state.quantity || state.quantity < 1) {
        setError('Please choose a quantity.');
        return;
      }
      setError('');
      submitBtn.disabled = true;
      const prevLabel = submitBtn.textContent;
      submitBtn.textContent = 'Adding…';
      try {
        const variant = state.selectedVariant;
        await addToCart({
          variantId: variant.id,
          quantity: state.quantity,
          meta: {
            productTitle: state.product?.title || state.triggerMeta.productTitle || 'Moveboard',
            variantTitle: variant.title || '',
            price: String(variant.price || 0),
            image: variantImage(variant),
            url: state.triggerMeta.url || `/products/${state.product?.handle || ''}`,
          },
        });
        if (getCustomerId()) {
          closeModal();
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not add to cart.');
      } finally {
        submitBtn.disabled = !state.selectedVariant?.available;
        submitBtn.textContent = prevLabel;
      }
    });

    document.querySelectorAll('[data-atc-open]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        openModal(btn);
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hidden) closeModal();
    });
  };

  document.querySelectorAll('[data-orders-open]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const accountPanel = document.querySelector('[data-account-menu-panel]');
      const accountToggle = document.querySelector('[data-account-menu-toggle]');
      if (accountPanel) accountPanel.hidden = true;
      if (accountToggle) accountToggle.setAttribute('aria-expanded', 'false');
      const mobileNav = document.querySelector('[data-mobile-nav]');
      const menuToggle = document.querySelector('[data-menu-toggle]');
      if (mobileNav) mobileNav.hidden = true;
      if (menuToggle) menuToggle.setAttribute('aria-expanded', 'false');
      openOrdersDrawer();
    });
  });

  const resumePendingCart = async () => {
    const params = new URLSearchParams(location.search);
    const resumeAdd = params.get('resume_cart') === '1';
    const resumeView = params.get('resume_cart_view') === '1';
    if (!getCustomerId()) return;

    const cleanUrl = new URL(location.href);
    let dirty = false;
    if (resumeAdd) {
      cleanUrl.searchParams.delete('resume_cart');
      dirty = true;
    }
    if (resumeView) {
      cleanUrl.searchParams.delete('resume_cart_view');
      dirty = true;
    }
    if (dirty) {
      window.history.replaceState({}, '', cleanUrl.pathname + cleanUrl.search + cleanUrl.hash);
    }

    if (resumeAdd) {
      let pending = null;
      try {
        pending = JSON.parse(sessionStorage.getItem(PENDING_CART_KEY) || 'null');
        sessionStorage.removeItem(PENDING_CART_KEY);
      } catch {
        pending = null;
      }
      if (pending?.variantId) {
        try {
          await addToCart({
            variantId: pending.variantId,
            quantity: pending.quantity || 1,
            meta: {
              productTitle: pending.productTitle,
              variantTitle: pending.variantTitle,
              price: pending.price,
              image: pending.image,
              url: pending.url || pending.path,
            },
          });
        } catch (err) {
          window.alert(err instanceof Error ? err.message : 'Could not add to cart.');
        }
        if (pending.path && pending.path !== getCartPath()) {
          window.location.href = pending.path;
        }
        return;
      }
    }

    if (resumeView) {
      navigateToCart();
    }
  };

  if (isCartEnabled) {
    if (isCartPage() && !getCustomerId()) {
      window.location.href = buildLoginUrl('resume_cart_view', getCartPath());
    }

    document.querySelectorAll('[data-cart-open]').forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        const mobileNav = document.querySelector('[data-mobile-nav]');
        const menuToggle = document.querySelector('[data-menu-toggle]');
        if (mobileNav) mobileNav.hidden = true;
        if (menuToggle) menuToggle.setAttribute('aria-expanded', 'false');
        navigateToCart();
      });
    });

    cartDrawer?.querySelectorAll('[data-cart-close]').forEach((el) => {
      el.addEventListener('click', () => closeCartDrawer());
    });

    cartEls?.checkoutReserve?.addEventListener('click', () => {
      closeCartDrawer();
      const orderBtn = document.querySelector('[data-order-open]');
      if (orderBtn && !orderBtn.disabled) {
        orderBtn.click();
        return;
      }
      window.alert('Open a product page and use Order now to place your reservation.');
    });

    initAtcModal();

    (async () => {
      if (isCartPage() && getCustomerId() && document.querySelector('[data-cart-empty-panel]')) {
        setCartEmptySyncUi({ title: 'Checking your account cart…', showCopy: false });
      }
      const result = await mergeCustomerCartFromServer();
      const merged = result?.merged;
      const apiAvailable = result?.apiAvailable !== false;
      if (merged && isCartPage()) {
        window.location.reload();
        return;
      }
      if (isCartPage() && document.querySelector('[data-cart-empty-panel]')) {
        const local = await fetchCart().catch(() => ({ item_count: 0 }));
        if ((local?.item_count || 0) > 0) {
          window.location.reload();
          return;
        }
        if (!apiAvailable) {
          setCartEmptySyncUi({
            title: 'Your cart is empty on this device',
            showCopy: true,
          });
        } else {
          setCartEmptySyncUi({ title: 'Your cart is empty', showCopy: true });
        }
      }
      await refreshCartBadge();
      await resumePendingCart();
    })();
  }

  /* -------- Orders -------- */
  const ordersDrawer = document.querySelector('[data-orders-drawer]');
  const ordersEls = ordersDrawer
    ? {
        loading: ordersDrawer.querySelector('[data-orders-loading]'),
        empty: ordersDrawer.querySelector('[data-orders-empty]'),
        list: ordersDrawer.querySelector('[data-orders-list]'),
        error: ordersDrawer.querySelector('[data-orders-error]'),
        note: ordersDrawer.querySelector('[data-orders-note]'),
      }
    : null;

  const setOrdersError = (message) => {
    if (!ordersEls?.error) return;
    ordersEls.error.hidden = !message;
    ordersEls.error.textContent = message || '';
  };

  const setOrdersNote = (message) => {
    if (!ordersEls?.note) return;
    ordersEls.note.hidden = !message;
    ordersEls.note.textContent = message || '';
  };

  const closeOrdersDrawer = () => {
    if (!ordersDrawer) return;
    ordersDrawer.classList.remove('is-open');
    document.body.classList.remove('mb-drawer-open');
    window.setTimeout(() => {
      ordersDrawer.hidden = true;
      ordersDrawer.setAttribute('aria-hidden', 'true');
    }, 260);
  };

  const orderBadgeClass = (status) => {
    if (status === 'cancelled') return 'mb-order-badge--cancelled';
    if (status === 'delivered') return 'mb-order-badge--delivered';
    if (status === 'shipped') return 'mb-order-badge--shipped';
    return 'mb-order-badge--placed';
  };

  const renderOrders = (orders) => {
    if (!ordersEls) return;
    ordersEls.loading.hidden = true;
    setOrdersError('');
    if (!orders.length) {
      ordersEls.empty.hidden = false;
      ordersEls.list.hidden = true;
      ordersEls.list.innerHTML = '';
      return;
    }
    ordersEls.empty.hidden = true;
    ordersEls.list.hidden = false;
    ordersEls.list.innerHTML = orders
      .map((order) => {
        const deliveryRow =
          order.status === 'cancelled'
            ? '<div>This order was cancelled.</div>'
            : order.status === 'delivered'
              ? `<div>Delivered on <strong>${formatDate(order.deliveredAt || order.createdAt)}</strong></div>`
              : `<div>Expected delivery <strong>${formatDate(order.expectedDeliveryAt)}</strong></div>`;
        const lines = (order.lineItems || [])
          .map(
            (line) =>
              `<li class="mb-order-card__line"><span>${escapeHtml(line.title)}</span><span>× ${escapeHtml(line.quantity)}</span></li>`,
          )
          .join('');
        const isPaid = order.cancelKind !== 'draft';
        const confirmCopy = isPaid
          ? 'Cancel this order? We’ll refund the full amount to your original payment method. Banks and UPI providers can take up to 5 working days to settle it.'
          : 'Cancel this reservation? Nothing has been charged, so there is nothing to refund.';
        const actions = order.canCancel
          ? `<div class="mb-order-card__actions" data-order-actions>
            <button type="button" class="mb-order-card__cancel" data-order-cancel="${escapeHtml(order.id)}">
              Cancel order
            </button>
          </div>
          <div class="mb-order-cancel-confirm" data-order-confirm hidden>
            <p class="mb-order-cancel-confirm__copy">${confirmCopy}</p>
            <div class="mb-order-cancel-confirm__row">
              <button type="button" class="mb-order-cancel-confirm__keep" data-order-cancel-abort>
                Keep order
              </button>
              <button type="button" class="mb-order-cancel-confirm__go" data-order-cancel-confirm="${escapeHtml(order.id)}">
                Yes, cancel
              </button>
            </div>
          </div>`
          : '';
        return `<li class="mb-order-card" data-order-row="${escapeHtml(order.id)}">
          <div class="mb-order-card__top">
            <div>
              <p class="mb-order-card__name">${escapeHtml(order.name || 'Order')}</p>
              <p class="mb-order-card__date">Placed ${formatDate(order.createdAt)}</p>
            </div>
            <span class="mb-order-badge ${orderBadgeClass(order.status)}">${escapeHtml(order.label || order.status)}</span>
          </div>
          <div class="mb-order-card__body">
            <ul class="mb-order-card__lines">${lines || '<li class="mb-order-card__line"><span>Moveboard order</span><span></span></li>'}</ul>
            <div class="mb-order-card__meta">${deliveryRow}</div>
            ${order.total ? `<div class="mb-order-card__total"><span>Total</span><strong>${escapeHtml(order.total)}</strong></div>` : ''}
            ${actions}
          </div>
        </li>`;
      })
      .join('');
  };

  const loadOrders = async () => {
    const customerId = getCustomerId();
    const email = getCustomerEmail();
    if (!customerId && !email) throw new Error('Sign in to view your orders.');

    /* Through the proxy Shopify supplies the identity; locally we must pass it. */
    const endpoint = ordersEndpoint(
      '/customer-orders',
      isLocalPreview ? { customerId, email } : {},
    );
    if (!endpoint) throw new Error('Orders API is not configured.');

    const res = await fetch(endpoint, { headers: { Accept: 'application/json' } });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok || !payload.ok) {
      throw new Error(payload.message || 'Unable to load orders.');
    }
    return payload.orders || [];
  };

  const refreshOrders = async () => {
    if (!ordersEls) return;
    try {
      renderOrders(await loadOrders());
    } catch (err) {
      ordersEls.loading.hidden = true;
      setOrdersError(err instanceof Error ? err.message : 'Unable to load orders.');
      ordersEls.empty.hidden = true;
    }
  };

  const requestCancellation = async (orderId) => {
    const endpoint = ordersEndpoint(
      '/cancel-order',
      isLocalPreview ? { customerId: getCustomerId(), email: getCustomerEmail() } : {},
    );
    if (!endpoint) throw new Error('Orders API is not configured.');

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderId }),
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok || !payload.ok) {
      throw new Error(payload.message || 'Could not cancel this order.');
    }
    return payload;
  };

  /* Confirmation lives inside the drawer so the refund terms are readable. */
  const toggleCancelConfirm = (card, showConfirm) => {
    const actions = card?.querySelector('[data-order-actions]');
    const confirm = card?.querySelector('[data-order-confirm]');
    if (!actions || !confirm) return;
    actions.hidden = showConfirm;
    confirm.hidden = !showConfirm;
  };

  ordersEls?.list?.addEventListener('click', async (event) => {
    const openBtn = event.target.closest('[data-order-cancel]');
    if (openBtn) {
      setOrdersError('');
      setOrdersNote('');
      toggleCancelConfirm(openBtn.closest('[data-order-row]'), true);
      return;
    }

    if (event.target.closest('[data-order-cancel-abort]')) {
      toggleCancelConfirm(event.target.closest('[data-order-row]'), false);
      return;
    }

    const confirmBtn = event.target.closest('[data-order-cancel-confirm]');
    if (!confirmBtn) return;

    const orderId = confirmBtn.getAttribute('data-order-cancel-confirm');
    const label = confirmBtn.textContent;
    confirmBtn.disabled = true;
    confirmBtn.textContent = 'Cancelling…';
    setOrdersError('');
    setOrdersNote('');

    try {
      const result = await requestCancellation(orderId);
      setOrdersNote(result.message || 'Your order is cancelled.');
      await refreshOrders();
    } catch (err) {
      setOrdersError(err instanceof Error ? err.message : 'Could not cancel this order.');
      confirmBtn.disabled = false;
      confirmBtn.textContent = label;
    }
  });

  const openOrdersDrawer = async () => {
    if (!ordersDrawer || !ordersEls) return;
    if (!getCustomerId()) return;

    ordersDrawer.hidden = false;
    ordersDrawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('mb-drawer-open');
    ordersEls.loading.hidden = false;
    ordersEls.empty.hidden = true;
    ordersEls.list.hidden = true;
    setOrdersError('');
    setOrdersNote('');
    requestAnimationFrame(() => ordersDrawer.classList.add('is-open'));

    await refreshOrders();
  };

  ordersDrawer?.querySelectorAll('[data-orders-close]').forEach((el) => {
    el.addEventListener('click', () => closeOrdersDrawer());
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (cartDrawer && !cartDrawer.hidden) closeCartDrawer();
    if (ordersDrawer && !ordersDrawer.hidden) closeOrdersDrawer();
  });

  /* Cart page — +/- quantity (no Update button) */
  const initCartPageQty = () => {
    const page = document.querySelector('[data-cart-page]');
    if (!page) return;

    const changeLineQty = async (lineKey, quantity) => {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ id: lineKey, quantity }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.description || payload?.message || 'Could not update quantity.');
      }
      return payload;
    };

    page.querySelectorAll('[data-cart-qty]').forEach((wrap) => {
      const lineKey = wrap.getAttribute('data-line-key');
      const max = Math.min(5, Math.max(1, Number(wrap.getAttribute('data-max')) || 5));
      const input = wrap.querySelector('[data-cart-qty-input]');
      const valueEl = wrap.querySelector('[data-cart-qty-value]');
      const decBtn = wrap.querySelector('[data-cart-qty-decrease]');
      const incBtn = wrap.querySelector('[data-cart-qty-increase]');
      if (!lineKey || !input || !valueEl || !decBtn || !incBtn) return;

      const currentQty = () => Math.max(0, Number(input.value) || 0);

      const applyQty = async (next) => {
        wrap.classList.add('is-loading');
        try {
          await changeLineQty(lineKey, next);
          const cart = await fetchCart().catch(() => null);
          if (cart) await syncCustomerCartToServer(cart);
          window.location.reload();
        } catch (err) {
          wrap.classList.remove('is-loading');
          window.alert(err instanceof Error ? err.message : 'Could not update quantity.');
        }
      };

      decBtn.addEventListener('click', () => {
        const qty = currentQty();
        applyQty(qty <= 1 ? 0 : qty - 1);
      });

      incBtn.addEventListener('click', () => {
        const qty = currentQty();
        if (qty >= max) return;
        applyQty(qty + 1);
      });
    });
  };

  initCartPageQty();
})();
